import { useEffect, useMemo, useState, type FC } from 'react';
import { Input, Modal, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import { CopyOutlined } from '@ant-design/icons';

import s from './create-pap-case-modal.module.scss';

interface CreatePapCaseValues {
  caseName: string;
  specialist: boolean;
  createdAt: Date;
}

interface CreatePapCaseModalProps {
  open: boolean;
  creating?: boolean;
  existingCaseNames: string[];
  patientName: string;
  patientDob?: Date;
  ptId: string;
  createdBy: string;
  onCancel: () => void;
  onCreate: (values: CreatePapCaseValues) => Promise<void> | void;
}

const normalizeName = (name: string) => name.trim().toLowerCase();

const CreatePapCaseModal: FC<CreatePapCaseModalProps> = ({
  open,
  creating = false,
  existingCaseNames,
  patientName,
  patientDob,
  ptId,
  createdBy,
  onCancel,
  onCreate,
}) => {
  const { t } = useTranslation();
  const [caseName, setCaseName] = useState('');
  const [specialist, setSpecialist] = useState<string>('');
  const [createdAt, setCreatedAt] = useState(new Date());
  const [touched, setTouched] = useState({
    caseName: false,
    specialist: false,
  });

  useEffect(() => {
    if (!open) return;
    setCaseName('');
    setSpecialist('');
    setTouched({ caseName: false, specialist: false });
    setCreatedAt(new Date());
  }, [open]);

  const isDuplicateName = useMemo(() => {
    if (!caseName.trim()) return false;
    const normalized = normalizeName(caseName);
    return existingCaseNames.some((name) => normalizeName(name) === normalized);
  }, [caseName, existingCaseNames]);

  const caseNameError = useMemo(() => {
    if (!touched.caseName) return '';
    if (!caseName.trim()) return 'Case Name is required';
    if (isDuplicateName) return 'Case Name must be unique for this patient';
    return '';
  }, [caseName, isDuplicateName, touched.caseName]);

  const specialistError = useMemo(() => {
    if (!touched.specialist) return '';
    if (!specialist) return 'Specialist selection is required';
    return '';
  }, [specialist, touched.specialist]);

  const isFormValid =
    !!caseName.trim() &&
    !isDuplicateName &&
    (specialist === 'yes' || specialist === 'no');

  const onSubmit = async () => {
    setTouched({ caseName: true, specialist: true });
    if (!isFormValid) return;

    await onCreate({
      caseName: caseName.trim(),
      specialist: specialist === 'yes',
      createdAt,
    });
  };

  const formattedDob = patientDob
    ? DateFormatter({ date: patientDob, format: DateFormats['MM/dd/yyyy'] })
    : '-';

  const formattedCreatedAt = DateFormatter({
    date: createdAt,
    format: DateFormats['MM/dd/yyyy'],
  });

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(ptId);
  };

  return (
    <Modal
      open={open}
      title={t('labels.createNewPapCase')}
      onCancel={onCancel}
      confirmLoading={creating}
      footer={null}
      destroyOnClose
      className={s.modal}
      width={520}
      centered
    >
      <div className={s.body}>
        <div className={s.field}>
          <label className={s.label}>
            {t('labels.caseName')}
            <span className={s.required}>*</span>
          </label>
          <Input
            value={caseName}
            onChange={(e) => setCaseName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, caseName: true }))}
            placeholder={t('labels.caseNamePlaceholder')}
            status={caseNameError ? 'error' : ''}
          />
          <p className={s.helper}>{t('labels.caseNameHelperText')}</p>
          {!!caseNameError && (
            <p className={s.error}>{String(caseNameError)}</p>
          )}
        </div>

        <div className={s.field}>
          <label className={s.label}>
            {t('labels.specialistQuestion')}
            <span className={s.required}>*</span>
          </label>
          <Select
            value={specialist || undefined}
            onChange={(value) => setSpecialist(value)}
            onBlur={() => setTouched((prev) => ({ ...prev, specialist: true }))}
            placeholder={t('labels.pleaseSelect')}
            options={[
              { label: t('controls.yes'), value: 'yes' },
              { label: t('controls.no'), value: 'no' },
            ]}
            status={specialistError ? 'error' : ''}
            className={s.fullWidth}
          />
          {!!specialistError && (
            <p className={s.error}>{String(specialistError)}</p>
          )}
        </div>

        <div className={s['detail-panel']}>
          <p className={s['detail-title']}>{t('labels.caseDetail')}</p>
          <div className={s['detail-grid']}>
            <p className={s['detail-section-label']}>{t('labels.patient')}</p>
            <p className={s['detail-value']}>{patientName || '-'}</p>
            <div className={s['detail-info-row']}>
              <span className={s['detail-info']}>
                {t('labels.dob')}: {formattedDob}
              </span>
              <span className={s.separator}> | </span>
              <span className={s['pt-id-label']}>{t('labels.ptID')}:</span>{' '}
              <span className={s['pt-id-value']}>{ptId}</span>
              <Tooltip title={t('controls.copy')}>
                <CopyOutlined
                  className={s['copy-icon']}
                  onClick={handleCopyToClipboard}
                />
              </Tooltip>
            </div>
          </div>

          <div className={s['detail-grid']}>
            <p className={s['detail-section-label']}>{t('labels.createdBy')}</p>
            <p className={s['detail-value']}>{createdBy || '-'}</p>
            <p className={s['detail-info']}>{formattedCreatedAt}</p>
          </div>
        </div>

        <div className={s.footer}>
          <BaseButton
            type="secondary"
            label={t('controls.cancel')}
            onClick={onCancel}
            disabled={creating}
          />
          <BaseButton
            label="Create"
            onClick={onSubmit}
            loading={creating}
            disabled={!isFormValid || creating}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreatePapCaseModal;
